import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Option } from './Option'
import { FKType } from './SQLBuilder'
import { User } from './User'

type PayeeConstructorArgs = ConstructorArgs<() => Payee>

export enum PAYEE_TYPE {
    PAYEE_TYPE_NONE,
    PAYEE_TYPE_ANY,
    PAYEE_TYPE_STAFF_ONLY,
    PAYEE_TYPE_PICKUP_STAFF_ONLY,
    PAYEE_TYPE_ANY_EMPLOYEE,
    PAYEE_TYPE_ANY_EMPLOYEE_OR_CONTRACTOR,
    PAYEE_TYPE_BOOKABLE_ONLINE,
}

export class Payee extends BaseRecord {
    readonly recordType = 'payee'

    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    skills: string[] = []
    rateType?: string
    classRateType?: string
    invoiceRateType?: string
    packageRateType?: string
    gratuityRateType?: string
    estimateRateType?: string
    reservationRateType?: string
    subscriptionRateType?: string
    rate?: number
    classRate?: number
    invoiceRate?: number
    packageRate?: number
    gratuityRate?: number
    estimateRate?: number
    reservationRate?: number
    subscriptionRate?: number
    color?: string
    notify?: string
    reminder?: string
    checkinReminder?: string
    sendSummary?: boolean // if TRUE, send a daily schedule summary
    showOnline?: boolean // if TRUE, let clients select this pro during checkout
    showIncome?: boolean // if TRUE, let this pro (admin) view unfiltered income dashboard
    showClients?: boolean // if TRUE, allow this staff member to view all clients (not just theirs)
    showInbox?: boolean // if TRUE, share super-admin inbox and all employee inboxes with this admin
    showCheckin?: boolean // if TRUE, let this pro check in and out of appointments/classes
    sendCheckin?: boolean // if TRUE, notify the client automatically during check in / check out
    showSelfie?: boolean // if TRUE, require selfie image during check in / check out
    allowReviewReply?: boolean // if TRUE, this pro is allowed to view & respond to reviews
    allowPickup?: boolean // if TRUE, this pro is allowed to pickup product orders
    allowPremium?: boolean // (not stored), set during new employee checkout to setup premium
    allowPayment?: boolean // (not stored), set during new employee checkout to setup payments
    allowCalendar?: boolean // (not stored), set during new employee checkout to setup calendar

    constructor(data?: Partial<PayeeConstructorArgs>, params?: { person?: User }) {
        super(data)
        if (!data) {
            this.sendSummary = true
            this.showOnline = true
            this.showIncome = false
            this.showInbox = false
            this.showClients = false
            this.allowPickup = false
            this.allowReviewReply = false
            this.person = params?.person
            this.creator = PSApplication.me()
            this.owner = this.creator.owner
            return
        }

        this.rate = this.propertyFromCurrencyArg(data.rate)
        this.classRate = this.propertyFromCurrencyArg(data.class_rate)
        this.packageRate = this.propertyFromCurrencyArg(data.package_rate)
        this.invoiceRate = this.propertyFromCurrencyArg(data.invoice_rate)
        this.gratuityRate = this.propertyFromCurrencyArg(data.gratuity_rate)
        this.estimateRate = this.propertyFromCurrencyArg(data.estimate_rate)
        this.reservationRate = this.propertyFromCurrencyArg(data.reservation_rate)
        this.subscriptionRate = this.propertyFromCurrencyArg(data.subscription_rate)
        this.rateType = data.rate_type ?? undefined
        this.classRateType = data.class_rate_type ?? undefined
        this.packageRateType = data.package_rate_type ?? undefined
        this.invoiceRateType = data.invoice_rate_type ?? undefined
        this.gratuityRateType = data.gratuity_rate_type ?? undefined
        this.estimateRateType = data.estimate_rate_type ?? undefined
        this.reservationRateType = data.reservation_rate_type ?? undefined
        this.subscriptionRateType = data.subscription_rate_type ?? undefined
        this.reminder = data.reminder ?? undefined
        this.checkinReminder = data.checkin_reminder ?? undefined
        this.color = data.color ?? undefined
        this.notify = data.notify ?? undefined
        this.sendSummary = data.send_summary ?? false
        this.showOnline = data.show_online ?? false
        this.showIncome = data.show_income ?? false
        this.showInbox = data.show_inbox ?? false
        this.showClients = data.show_clients ?? false
        this.showCheckin = data.show_checkin ?? false
        this.sendCheckin = data.send_checkin ?? false
        this.showSelfie = data.show_selfie ?? false
        this.allowPickup = data.allow_pickup ?? false
        this.allowReviewReply = data.allow_review_reply ?? false
        this.skills = data.skills ?? []
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<PayeeConstructorArgs>): Payee {
        return new Payee({ ...this.serializeForClone(), ...overrides })
    }

    get notifyType(): string | undefined {
        return this.notify
    }

    static rateStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: 'fixed', label: 'Fixed' }))
        strings.push(new Option(undefined, { id: 'percent', label: 'Percentage' }))
        strings.push(new Option(undefined, { id: 'hourly', label: 'Hourly' }))
        return strings
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.person) this.person = BaseRecord.fromSerializer(data.person, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner', 'person', 'creator'],
    }

    static dataType(): DataType {
        return 'payees'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['person'] = this.person?.id ?? ''
        dict['notify'] = this.notify ?? ''
        dict['reminder'] = this.reminder ?? ''
        dict['checkin_reminder'] = this.checkinReminder ?? ''
        dict['rate'] = this.rate ?? ''
        dict['class_rate'] = this.classRate ?? ''
        dict['invoice_rate'] = this.invoiceRate ?? ''
        dict['package_rate'] = this.packageRate ?? ''
        dict['gratuity_rate'] = this.gratuityRate ?? ''
        dict['estimate_rate'] = this.estimateRate ?? ''
        dict['reservation_rate'] = this.reservationRate ?? ''
        dict['subscription_rate'] = this.subscriptionRate ?? ''
        dict['rate_type'] = this.rateType ?? ''
        dict['class_rate_type'] = this.classRateType ?? ''
        dict['package_rate_type'] = this.packageRateType ?? ''
        dict['invoice_rate_type'] = this.invoiceRateType ?? ''
        dict['gratuity_rate_type'] = this.gratuityRateType ?? ''
        dict['estimate_rate_type'] = this.estimateRateType ?? ''
        dict['reservation_rate_type'] = this.reservationRateType ?? ''
        dict['subscription_rate_type'] = this.subscriptionRateType ?? ''
        dict['color'] = this.color ?? ''
        dict['send_summary'] = this.sendSummary
        dict['show_online'] = this.showOnline
        dict['show_income'] = this.showIncome
        dict['show_inbox'] = this.showInbox
        dict['show_clients'] = this.showClients
        dict['show_checkin'] = this.showCheckin
        dict['send_checkin'] = this.sendCheckin
        dict['show_selfie'] = this.showSelfie
        dict['allow_pickup'] = this.allowPickup
        dict['allow_review_reply'] = this.allowReviewReply
        dict['skills'] = this.skills?.length > 0 ? this.skills : ''
        return dict
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get person(): User | undefined {
        return this.getForeignRecord('person', User)
    }
    set person(person: User | undefined) {
        this.setForeignRecord('person', person)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
}
